import { memo, type CSSProperties, type ReactNode } from 'react';
import type { Card } from '@gooflord/shared';
import { artUrl } from '../lib/api';
import { cls } from '../lib/util';

export interface CardViewProps {
  card?: Card;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  level?: number;          // displayed level (with buffs); defaults to printed level
  boosted?: boolean;       // level above printed
  weakened?: boolean;      // level below printed
  exhausted?: boolean;
  insured?: boolean;
  qty?: number;
  selected?: boolean;
  targetable?: boolean;
  dim?: boolean;
  faceDown?: boolean;
  back?: string;           // card back style for face-down cards
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  label?: string;          // extra screen-reader context
}

/** One Gooflord card. Ben's scan when there is one, a drawn placeholder otherwise. Always a button when clickable, always labelled. */
export const CardView = memo(function CardView({ card, size = 'md', level, boosted, weakened, exhausted, insured, qty, selected, targetable, dim, faceDown, back, onClick, className, style, children, label }: CardViewProps) {
  if (faceDown) {
    return <div className={cls('card', size, 'facedown', back === 'neon' && 'neon', className)} style={style} aria-hidden="true">{children}</div>;
  }
  if (!card) return <div className={cls('card', size, 'empty', className)} style={style} />;
  const art = artUrl(card.art);
  const lv = level ?? card.level;
  const showLevel = card.type !== 'item' && card.type !== 'gooflord' || card.ability === 'summon';
  const aria = [card.name, showLevel ? `level ${lv}` : card.type, exhausted ? 'exhausted' : '', insured ? 'insured with Green Potion' : '', boosted ? 'boosted' : weakened ? 'weakened' : '', label ?? ''].filter(Boolean).join(', ');
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick} aria-label={onClick ? aria : undefined} aria-pressed={onClick && selected != null ? selected : undefined} title={card.text || card.flavor}
      className={cls('card', size, card.type, art && 'has-art', selected && 'selected', targetable && 'targetable', exhausted && 'exhausted', dim && 'dim', onClick && 'clickable', className)} style={style}>
      {art ? <img className="art" src={art} alt="" draggable={false} loading="lazy" /> : <div className="art-fallback" style={{ '--h': card.hue ?? 200 } as CSSProperties}><span>{card.emoji ?? '✨'}</span></div>}
      {!art && <div className="cap-name">{card.name}<small>{card.type}</small></div>}
      {art && <div className="cap">{card.name}</div>}
      {showLevel && <div className={cls('lvl', boosted && 'up', weakened && 'down')} aria-hidden="true">{boosted ? '▲' : weakened ? '▼' : ''}{lv}</div>}
      {qty != null && <div className="qty" aria-label={`${qty} owned`}>×{qty}</div>}
      {insured && <div className="tag" aria-hidden="true">🧪</div>}
      {!onClick && <span className="sr-only">{aria}</span>}
      {children}
    </Tag>
  );
});

export const RarityPill = ({ rarity }: { rarity: string }) => <span className={cls('pill', rarity)}>{rarity}</span>;
